import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, ChevronUp, ChevronDown } from "lucide-react";

// Per-user (localStorage) column visibility + order for a table.
// allColumns: [{ key, label, right?, center?, render? }]
export function useTableColumns(storageKey, allColumns) {
  const allKeys = allColumns.map((c) => c.key);
  const load = () => {
    try { const v = JSON.parse(localStorage.getItem(storageKey)); if (Array.isArray(v)) { const f = v.filter((k) => allKeys.includes(k)); if (f.length) return f; } } catch { /* ignore */ }
    return allKeys;
  };
  const [visible, setV] = useState(load);
  const setVisible = (v) => { setV(v); try { localStorage.setItem(storageKey, JSON.stringify(v)); } catch { /* ignore */ } };
  const reset = () => setVisible(allKeys);
  const map = Object.fromEntries(allColumns.map((c) => [c.key, c]));
  const cols = visible.map((k) => map[k]).filter(Boolean);
  return { cols, visible, setVisible, reset, allColumns };
}

export function ColumnChooser({ ctl, testid = "column-chooser" }) {
  const { visible, setVisible, reset, allColumns } = ctl;
  const toggle = (k) => setVisible(visible.includes(k) ? visible.filter((x) => x !== k) : [...visible, k]);
  const move = (k, dir) => {
    const i = visible.indexOf(k), j = i + dir;
    if (i < 0 || j < 0 || j >= visible.length) return;
    const v = [...visible]; [v[i], v[j]] = [v[j], v[i]]; setVisible(v);
  };
  const hidden = allColumns.filter((c) => !visible.includes(c.key));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 bg-white" data-testid={`${testid}-btn`}><SlidersHorizontal className="w-4 h-4" /> Columns</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid={`${testid}-panel`}>
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="text-[11px] font-semibold text-[#6B7189] uppercase tracking-wide">Columns</span>
          <button onClick={reset} className="text-xs text-[#1D4ED8] font-medium" data-testid={`${testid}-reset`}>Reset</button>
        </div>
        <div className="space-y-0.5 max-h-72 overflow-y-auto">
          {visible.map((k) => {
            const c = allColumns.find((a) => a.key === k); if (!c) return null;
            return (
              <div key={k} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-[#F5F6FA]" data-testid={`${testid}-opt-${k}`}>
                <input type="checkbox" checked onChange={() => toggle(k)} className="accent-[#101D51]" data-testid={`${testid}-check-${k}`} />
                <span className="text-sm text-[#101D51] flex-1 truncate">{c.label}</span>
                <button onClick={() => move(k, -1)} className="text-[#6B7189] hover:text-[#101D51]" data-testid={`${testid}-up-${k}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(k, 1)} className="text-[#6B7189] hover:text-[#101D51]" data-testid={`${testid}-down-${k}`}><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>);
          })}
          {hidden.length > 0 && <div className="border-t border-[#F0F1F5] my-1" />}
          {hidden.map((c) => (
            <div key={c.key} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-[#F5F6FA]" data-testid={`${testid}-opt-${c.key}`}>
              <input type="checkbox" checked={false} onChange={() => toggle(c.key)} className="accent-[#101D51]" data-testid={`${testid}-check-${c.key}`} />
              <span className="text-sm text-[#6B7189] flex-1 truncate">{c.label}</span>
            </div>))}
        </div>
      </PopoverContent>
    </Popover>);
}
